import { Injectable, Inject, Component } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, Subscription } from 'rxjs';
import { Store } from '@ngrx/store';

import { Broker, BrokerSubscriber, BrokerRequest } from '@c4/core';

import * as moment from 'moment';

import { DeviceCategoryService } from './device-category.service';
import { ItemsService, itemsReducer, ItemsState as ItemsContext } from './items.service';
import { ProgrammingUtilsService as Utils, TAG_QSP, CW_CREATOR_ID } from './utils.service';

import { Command } from './../params/interfaces/command.interface';
import { Event, DeviceEvent } from '../interfaces/event.interface';
import { Location, Room, Device } from '../interfaces/item.interface';
import { LocationsService } from './locations.service';
import { CodeItem } from './../interfaces/code-item.interface';

import { ScheduleProgrammingService, ScheduleEvent } from '../../quickstarts/quickstart-schedule/services/schedule.service';

export interface CommonProgrammingContext {
  initialized: boolean;
  ready: boolean;
  error: boolean;
  editing: boolean;
  listExpanded: boolean;
  eventExpanded: boolean;
  adding: boolean;
  errors?: Array<any>;
  searchText: string;
  saved: boolean;
  viewTitle: string;

  floors?: Array<any>;
  rooms?: Array<any>;
  filteredDevices?: Array<any>;
  currentFloor?: any;
  currentRoom?: any;

  agents?: Array<any>;
  categories?: Array<string>;

  flatList?: Array<any>;
  groupedList?: Array<{
    label: string;
    events: Array<any>
  }>;
  roomList?: Array<any>;
  roomMap?: any;
  currentGrouping: string;
  hasProtected: boolean;

  trigger: {
    devices?: Array<Device>,
    device?: Device,
    events?: Array<Event>,
    event?: Event | ScheduleEvent
  };
  action: {
    devices?: Array<Device>,
    device?: Device,
    commands?: Array<any>,
    command?: any
  };
}

export interface CommandData {
  codeitemId: number;
  deviceId: number;
  command: string;
  beforeCodeItemId: number;
  parentCodeItemId: number;
  creator: string;
  type: number;
  params?: {
    name: string,
    value: string | number
  }[];
}

export const INITAL_COMMON_STATE: CommonProgrammingContext = {
  initialized: false,
  ready: false,
  error: false,
  editing: false,
  adding: false,
  saved: false,
  listExpanded: true,
  eventExpanded: true,
  currentGrouping: 'room',
  searchText: '',
  hasProtected: false,
  viewTitle: 'Back',
  trigger: {},
  action: {}
};

const STORE_NAME: string = 'PROGRAMMING:';

const SET_INITIALIZED: string = STORE_NAME + 'SET_INITIALIZED';
const SET_READY: string = STORE_NAME + 'SET_READY';
const SET_ERROR: string = STORE_NAME + 'SET_ERROR';
const CLEAR_ERROR: string = STORE_NAME + 'CLEAR_ERROR';

const SET_CATEGORIES: string = STORE_NAME + 'SET_CATEGORIES';
const SET_AGENTS: string = STORE_NAME + 'SET_AGENTS';
const SET_ROOMS_WITH_EVENTS: string = STORE_NAME + 'SET_ROOMS_WITH_EVENTS';
const SET_SEARCH_TEXT: string = STORE_NAME + 'SET_SEARCH_TEXT';
const SET_SHOWING: string = STORE_NAME + 'SET_SHOWING';
const SET_GROUPING: string = STORE_NAME + 'SET_GROUPING';
const SET_SELECTED_EVENT: string = STORE_NAME + 'SET_SELECTED_EVENT';
const CLEAR_SELECTED_EVENT: string = STORE_NAME + 'CLEAR_SELECTED_EVENT';
const SET_FLAT_LIST: string = STORE_NAME + 'SET_FLAT_LIST';
const SET_GROUPED_LIST: string = STORE_NAME + 'SET_GROUPED_LIST';
const SET_EVENT_DEVICE: string = STORE_NAME + 'SET_EVENT_DEVICE';
const SET_HAS_PROTECTED: string = STORE_NAME + 'SET_HAS_PROTECTED';

const SET_TRIGGER_DEVICES: string = STORE_NAME + 'SET_TRIGGER_DEVICES';
const SET_TRIGGER_DEVICE: string = STORE_NAME + 'SET_TRIGGER_DEVICE';
const SET_TRIGGER_EVENTS: string = STORE_NAME + 'SET_TRIGGER_EVENTS';
const SET_TRIGGER_EVENT: string = STORE_NAME + 'SET_TRIGGER_EVENT';

const SET_ACTION_DEVICES: string = STORE_NAME + 'SET_ACTION_DEVICES';
const SET_ACTION_DEVICE: string = STORE_NAME + 'SET_ACTION_DEVICE';
const SET_ACTION_COMMANDS: string = STORE_NAME + 'SET_ACTION_COMMANDS';
const SET_ACTION_COMMAND: string = STORE_NAME + 'SET_ACTION_COMMAND';
export const SET_COMMAND_PARAMS: string = STORE_NAME + 'SET_COMMAND_PARAMS';

const SET_FLOORS: string = STORE_NAME + 'SET_FLOORS';
const SET_ROOMS: string = STORE_NAME + 'SET_ROOMS';
const SET_LOCATION_DEVICES: string = STORE_NAME + 'SET_LOCATION_DEVICES';
const SET_FLOOR: string = STORE_NAME + 'SET_FLOOR';
const SET_ROOM: string = STORE_NAME + 'SET_ROOM';

const SET_LIST_EXPANDED: string = STORE_NAME + 'SET_LIST_EXPANDED';
const SET_EVENT_EXPANDED: string = STORE_NAME + 'SET_EVENT_EXPANDED';
const TOGGLE_LIST_EXPANDED: string = STORE_NAME + 'TOGGLE_LIST_EXPANDED';
const TOGGLE_EVENT_EXPANDED: string = STORE_NAME + 'TOGGLE_EVENT_EXPANDED';
const SET_VIEW_TITLE: string = STORE_NAME + 'SET_VIEW_TITLE';
const SET_EDITING: string = STORE_NAME + 'SET_EDITING';

export function sharedReducer(state: CommonProgrammingContext = INITAL_COMMON_STATE, { type, payload }) {
  // if (type.indexOf(STORE_NAME) > -1) {
  //   console.log(type, payload);
  // }

  switch (type) {
    // general purpose
    case SET_INITIALIZED: return Object.assign({}, state, { initialized: payload });
    case SET_READY: return Object.assign({}, state, { ready: payload });
    case SET_CATEGORIES: return Object.assign({}, state, { categories: payload });
    case SET_SEARCH_TEXT: return Object.assign({}, state, { searchText: payload });
    case SET_ROOMS_WITH_EVENTS: return Object.assign({}, state, { groupedByRoom: payload });
    case SET_AGENTS: return Object.assign({}, state, { agents: payload });
    case SET_CATEGORIES: return Object.assign({}, state, { categories: payload });
    case SET_SHOWING: return Object.assign({}, state, { currentShowing: payload });
    case SET_GROUPING: return Object.assign({}, state, { currentGrouping: payload });
    case SET_SELECTED_EVENT: return Object.assign({}, state, { trigger: Object.assign({}, state.trigger, { event: payload }) });
    case CLEAR_SELECTED_EVENT: return Object.assign({}, state, { trigger: Object.assign({}, state.trigger, { event: undefined }) });
    case SET_EVENT_DEVICE: return Object.assign({}, state, { eventDevice: payload });
    case SET_HAS_PROTECTED: return Object.assign({}, state, { hasProtected: payload });

    case SET_LIST_EXPANDED: return Object.assign({}, state, { listExpanded: payload });
    case TOGGLE_LIST_EXPANDED: return Object.assign({}, state, { listExpanded: !state.listExpanded });
    case SET_EVENT_EXPANDED: return Object.assign({}, state, { eventExpanded: payload });
    case TOGGLE_EVENT_EXPANDED: return Object.assign({}, state, { eventExpanded: !state.eventExpanded });

    case SET_EDITING: return Object.assign({}, state, { editing: payload });
    case SET_VIEW_TITLE: return Object.assign({}, state, { viewTitle: payload });

    case SET_ROOMS: return Object.assign({}, state, { rooms: payload });
    case SET_FLOORS: return Object.assign({}, state, { floors: payload });
    case SET_LOCATION_DEVICES: return Object.assign({}, state, { filteredDevices: payload });
    case SET_ROOM: return Object.assign({}, state, { currentRoom: payload });
    case SET_FLOOR: return Object.assign({}, state, { currentFloor: payload });

    case SET_TRIGGER_DEVICES: return Object.assign({}, state, { trigger: Object.assign({}, state.trigger, { devices: payload }) });
    case SET_TRIGGER_DEVICE: return Object.assign({}, state, { trigger: Object.assign({}, state.trigger, { device: payload }) });
    case SET_TRIGGER_EVENTS: return Object.assign({}, state, { trigger: Object.assign({}, state.trigger, { events: payload }) });
    case SET_TRIGGER_EVENT: return Object.assign({}, state, { trigger: Object.assign({}, state.trigger, { event: payload }) });

    case SET_ACTION_DEVICES: return Object.assign({}, state, { action: Object.assign({}, state.action, { devices: payload }) });
    case SET_ACTION_DEVICE: return Object.assign({}, state, { action: Object.assign({}, state.action, { device: payload }) });
    case SET_ACTION_COMMANDS: return Object.assign({}, state, { action: Object.assign({}, state.action, { commands: payload }) });
    case SET_ACTION_COMMAND: return Object.assign({}, state, { action: Object.assign({}, state.action, { command: payload }) });
    case SET_COMMAND_PARAMS: return Object.assign({}, state, {
      action: Object.assign({}, state.action, {
        command: Object.assign({}, state.action.command, { params: payload })
      })
    });

    case SET_ERROR: return Object.assign({}, state, { error: true, errors: payload });
    case CLEAR_ERROR: return Object.assign({}, state, { error: false, errors: undefined });

    default: return state;
  }
};

@Injectable()
export class SharedProgrammingService {
  searchText: Observable<string>;
  isReady: Observable<boolean>;
  currentGrouping: Observable<string>;
  initialized: Observable<boolean>;
  listExpanded: Observable<boolean>;
  eventExpanded: Observable<boolean>;
  viewTitle: Observable<string>;

  private _itemsEventsSub: BrokerSubscriber<Event[]>;

  constructor(
    private broker: Broker,
    private router: Router,
    private store: Store<{
      sharedProgramming: CommonProgrammingContext,
      programmingItems: ItemsContext
    }>,
    private deviceCategories: DeviceCategoryService,
    private items: ItemsService,
    private schedule: ScheduleProgrammingService,
    private utils: Utils,
    private locations: LocationsService
  ) {
    this.broker.connected.subscribe(connected => {
      if (connected) {
        this.ready(false);
        Promise.all([
          this.getCategories(),
          this._getAgents()
        ]).then(res => {
          console.log('shared services init complete');
          this.store.dispatch({ type: SET_INITIALIZED, payload: true });
        }, err => {
          console.error('error initalizing shared programming service', err);
          this.error(err);
          this.ready(true);
        });
      }
    });

    this.searchText = this.store.select(s => s.sharedProgramming.searchText);
    this.isReady = this.store.select(s => s.sharedProgramming.ready);
    this.currentGrouping = this.store.select(s => s.sharedProgramming.currentGrouping);
    this.initialized = this.store.select(s => s.sharedProgramming.initialized);
    this.viewTitle = this.store.select(s => s.sharedProgramming.viewTitle);
    this.listExpanded = this.store.select(s => s.sharedProgramming.listExpanded);
    this.eventExpanded = this.store.select(s => s.sharedProgramming.eventExpanded);
  }

  setListExpanded(state: boolean) {
    this.store.dispatch({ type: SET_LIST_EXPANDED, payload: state });
  }

  toggleListExpanded() {
    this.store.dispatch({ type: TOGGLE_LIST_EXPANDED });
  }

  setEventExpanded(state: boolean) {
    this.store.dispatch({ type: SET_EVENT_EXPANDED, payload: state });
  }

  toggleEventExpanded() {
    this.store.dispatch({ type: TOGGLE_EVENT_EXPANDED });
  }

  setEditing(state: boolean) {
    this.store.dispatch({ type: SET_EDITING, payload: state });
  }

  setSearchText(text: string) {
    this.store.dispatch({ type: SET_SEARCH_TEXT, payload: text });
  }

  setGrouping(value: string) {
    this.store.dispatch({ type: SET_GROUPING, payload: value });
  }

  ready(state: boolean) {
    // console.log('setting ready: ', state);
    this.store.dispatch({ type: SET_READY, payload: state });
  }

  error(error: any) {
    console.error('shared-programming: error:', error);
    this.store.dispatch({ type: SET_ERROR, payload: { error: true, errors: [error] } });
  }

  clearError() {
    this.store.dispatch({ type: CLEAR_ERROR });
  }

  private getCategories(): Promise<any> {
    return this.broker.call({
      path: '/api/v1/categories'
    }).then(res => {
      this.store.dispatch({ type: SET_CATEGORIES, payload: res });
    });
  }

  setSelectedEvent(event: any): Promise<Event> {
    return new Promise<Event>((resolve, reject) => {
      // console.log('shared: setting selected event', event);
      this.ready(false);
      if (event) {
        // HACK set protected state of the event and codeitems the same way we so when we process the entire list.
        event = this.utils.resolveProtected([event])[0];
        this.store.dispatch({ type: SET_SELECTED_EVENT, payload: event });
        if (event.deviceId) {
          this.broker.call({
            path: '/api/v1/items/' + event.deviceId
          }).then(res => {
            // console.log('shared: device for selected event is', res[0]);
            if (res.length > 0) {
              this.store.dispatch({ type: SET_EVENT_DEVICE, payload: res[0] });
            }
            this.ready(true);
            resolve(event);
          }, err => {
            console.error('shared: error getting event device', err);
            this.error(err);
            this.ready(true);
            reject(err);
          });
        }
      } else {
        this.ready(true);
        resolve(event);
      }
    });
  }

  clearSelectedEvent() {
    this.store.dispatch({ type: CLEAR_SELECTED_EVENT });
    if (this._triggerSubscriber) {
      this._triggerSubscriber.unsubscribe();
      this._triggerSubscriber = undefined;
    }
  }

  setTitle(title: string) {
    this.store.dispatch({ type: SET_VIEW_TITLE, payload: title });
  }

  setCurrentFloor(floorId: number) {
    let floor = this.locations.getLocationById(floorId);
    console.log('shared: setting floor', floor);
    if (floor) {
      this.store.dispatch({ type: SET_FLOOR, payload: floor });
    } else {
      this.store.dispatch({ type: SET_FLOOR, payload: undefined });
    }
  }

  setCurrentRoom(roomId: number) {
    let room = this.locations.getLocationById(roomId);
    console.log('shared: setting room', room);
    if (room) {
      this.store.dispatch({ type: SET_ROOM, payload: room });
    } else {
      this.store.dispatch({ type: SET_ROOM, payload: undefined });
    }
  }

  clearLocationFilter() {
    this.store.dispatch({ type: SET_FLOOR, payload: 'any' });
    this.store.dispatch({ type: SET_ROOM, payload: 'any' });
  }

  // getDevices(query?: any) {
  //   let req: BrokerRequest = {
  //     path: '/api/v1/items'
  //   }
  //   let reqQuery: any = { type: 7 };
  //   let floor = Utils.snapshot<Location>(this.store.select(s => s.sharedProgramming.currentFloor));
  //   if (floor) {
  //     reqQuery.floorId = floor.id;
  //   }
  //   let room = Utils.snapshot<Location>(this.store.select(s => s.sharedProgramming.currentRoom));
  //   if (room) {
  //     reqQuery.roomId = room.id;
  //   }
  //   if (query) {
  //     reqQuery = Object.assign({}, reqQuery, query);
  //   }
  //   req.queryString = {
  //     query: reqQuery
  //   };

  //   this.broker.call(req).then(res => {
  //     this.store.dispatch({ type: SET_LOCATION_DEVICES, payload: res });
  //   }, err => {
  //     this.error(err);
  //     this.store.dispatch({ type: SET_LOCATION_DEVICES, payload: [] });
  //   })
  // }

  toggleCodeitemEnabled(codeitem: CodeItem) {
    let event: Event = Utils.snapshot<Event>(this.store.select(s => s.sharedProgramming.trigger.event));
    this.broker.call({
      path: '/api/v1/items/' + event.deviceId + '/events/' + event.eventId + '/' + codeitem.codeItemId,
      method: 'POST',
      data: { enabled: !codeitem.enabled }
    }).then(res => {
      // HACK special case for schedule events
      if (event.deviceId === 100100) {
        console.log('shared: refreshing schedule event after toggling code item');
        this.schedule.refreshEvent(event.eventId).then(res => {
          this.setSelectedEvent(res);
        });
      }
    });
  }

  // TODO encapsulate this in a service
  private _getAgents(): Promise<any> {
    try {
      this.clearError();
      this.ready(false);
      // NOTE need to use an items query here because /api/v1/agents only returns agents
      // that have routes defined in the api
      return this.broker.call({
        // path: '/api/v1/items',
        // queryString: {
        //   query: { type: 9 }
        // }
        path: '/api/v1/agents'
      }).then(
        agents => {
          this.store.dispatch({ type: SET_AGENTS, payload: agents });
        },
        err => {
          this.error(err);
        });
    } catch (error) {
      this.error(error);
    } finally {
      this.ready(true);
    }
  }

  ////-------------------------

  private _triggerSubscriber: Subscription;

  fetchEvent(deviceId: number, eventId: number) {
    this.ready(false);
    if (deviceId === 100100) {
      this.schedule.refreshEvent(eventId).then(res => {
        this.setSelectedEvent(res).then(res => this.ready(true));
      });
    } else {
      this._triggerSubscriber = this.broker.getObservable({
        path: '/api/v1/items/' + deviceId + '/events/' + eventId
      }).subscribe(
        updates => this._handleTriggerUpdates(updates),
        err => console.error('shared: error subscribing to trigger event definition', err));
    }
  }

  private _handleTriggerUpdates(updates: any) {
    // console.log('shared: handling updated trigger event definition', updates);
    if (Array.isArray(updates)) {
      this.setSelectedEvent(updates[0]);
    }
    this.ready(true);
  }

  triggerEvent(): Promise<any> {
    this.clearError();
    let event = Utils.snapshot<Event>(this.store.select(s => s.sharedProgramming.trigger.event));
    return this.broker.call({
      method: 'PUT',
      path: '/api/v1/items/' + event.deviceId + '/events/' + event.eventId
    }).then(res => {
      console.log('shared: event invocation complete', res);
    }, err => {
      console.error('shared: error invoking event', err);
      this.error(err);
    });
  }

  deleteAction(action: any) {
    this.ready(false);
    let event = Utils.snapshot<Event>(this.store.select(s => s.sharedProgramming.trigger.event));
    if (event && event.eventId && event.deviceId && action) {
      let p = undefined;
      if (event.deviceId === 100100) {
        this.schedule.deleteAction(event, action).then(res => {
          this.schedule.refreshEvent(event.eventId).then(res => {
            this.setSelectedEvent(res);
            this.ready(true);
          }, err => {
            console.error('shared: error refreshing schedule event after deleting action', err);
            this.error(err);
            this.ready(true);
          });
        }, err => {
          console.error('shared: error deleting schedule action', err);
          this.error(err);
          this.ready(true);
        });
      } else {
        this._deleteAction(event, action).then(res => {
          this.ready(true);
        }, err => {
          console.error('shared: error deleting generic event action', err);
          this.error(err);
          this.ready(true);
        });
      }
    } else {
      console.warn('shared: no event in context when deleting action');
      this.ready(true);
      // return Promise.reject({ code: 'INTERNAL', message: 'no event in context when deleting actions' });
    }
  }

  private _deleteAction(event: any, action: any): Promise<any> {
    console.log('shared: deleting action', action);
    return new Promise<any>((resolve, reject) => {
      this.broker.call({
        method: 'DELETE',
        path: '/api/v1/items/' + event.deviceId + '/events/' + event.eventId + '/' + action.codeItemId
      }).then(res => {
        resolve(res);
      }, err => {
        reject(err);
      });
    });
  }

  deleteAllActions(): Promise<any> {
    let event = Utils.snapshot<Event>(this.store.select(s => s.sharedProgramming.trigger.event));
    if (event && event.codeItems) {
      let p = [];
      event.codeItems.forEach(ci => {
        let data = JSON.parse(ci.creatorState || '{}');
        // TODO remove tags check once we get closer to release (2.10)
        if (ci.creator === CW_CREATOR_ID || (!!data.tags && data.tags.indexOf(TAG_QSP) >= 0)) {
          p.push(this.deleteAction(ci));
        }
      });
    } else {
      return new Promise<any>((resolve, reject) => reject({ code: 'INTERNAL', message: 'no event in context when deleting actions' }));
    }
  }

  private _navigateToCurrentEvent(state: string = 'edit') {
    let event = Utils.snapshot<Event>(this.store.select(s => s.sharedProgramming.trigger.event));
    if (event) {
      this.router.navigate(['when-then', 'device', event.deviceId, 'event', event.eventId, state], { relativeTo: this.moduleRoot });
    }
  }

  saveAction(action: any): Promise<any> {
    this.ready(false);

    // HACK even though non-command actions are not yet supported, require action.type === 1 to continue
    action.type = action.type || 1;
    action.creator = CW_CREATOR_ID;
    let event = Utils.snapshot<Event>(this.store.select(s => s.sharedProgramming.trigger.event));
    action.creatorState = this.utils.buildCreatorState(event, action);
    // NOTE whichever step fails is responsible for setting the error context
    if (event.deviceId === 100100) {
      console.log('shared: delegating to schedule service to save action');
      return this.schedule.saveAction(event, action).then(res => {
        this.setSelectedEvent(res)
        this._navigateToCurrentEvent('edit');
        this.ready(true);
      }, err => {
        console.error('shared: error saving schedule event actions', err);
        this.ready(true);
      });
    } else if (event.eventId && event.deviceId) {
      return this._saveAction(event, action).then(
        res => this._navigateToCurrentEvent('edit'),
        err => console.error('shared: error saving action', err));
    } else {
      this.error({ type: 'INTERNAL', message: 'invalid context: no eventId or deviceId' });
      this.ready(true);
    }
  }

  private _saveAction(event: any, action: any): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      this.broker.call({
        method: 'POST',
        path: '/api/v1/items/' + event.deviceId + '/events/' + event.eventId + '/' + this.getActionType(action),
        data: action
      }).then(res => {
        resolve(res);
      }, err => {
        this.error(err);
        reject(err);
      });
    });
  }

  private getActionType(action: any): string {
    switch (action.type) {
      case 1: return 'commands';
      case 2: return 'conditionals';
      case 3: return 'loops';

      default: throw new Error('invalid action type on save: ' + JSON.stringify(action));
    }
  }

  getTriggerDevices(query: any) {
    if (query) {
      this.broker.call({
        path: '/api/v1/items',
        queryString: {
          query: query
        }
      }).then(res => {
        this.store.dispatch({ type: SET_TRIGGER_DEVICES, payload: res });
      }, err => {
        this.error(err);
      });
    }
  }

  getActionDevices(query: any) {
    if (query) {
      this.broker.call({
        path: '/api/v1/items',
        queryString: {
          query: query
        }
      }).then(res => {
        this.store.dispatch({ type: SET_ACTION_DEVICES, payload: res });
      }, err => {
        this.error(err);
      });
    }
  }

  setTriggerDevice(device: Device) {
    // NOTE clear the trigger events first so they don't stick around
    // once the new device is set and new event list retrieved
    // TODO could do this in the reducer I guess
    this.store.dispatch({ type: SET_TRIGGER_EVENTS, payload: [] });

    this.store.dispatch({ type: SET_TRIGGER_DEVICE, payload: device });
    this._getTriggerEvents(device);
  }

  private _getTriggerEvents(device: Device) {
    this.broker.call({
      path: '/api/v1/items/' + device.id + '/events',
      queryString: {
        allevents: true
      }
    }).then(res => {
      this.store.dispatch({ type: SET_TRIGGER_EVENTS, payload: res });
    }, err => {
      this.error(err);
    });
  }

  setSelectedAction(action: Command) {
    this.store.dispatch({ type: SET_ACTION_COMMAND, payload: action });
  }

  setActionDevice(device: Device) {
    this.store.dispatch({ type: SET_ACTION_DEVICE, payload: device });
    this._getActionCommands(device);
  }

  private _getActionCommands(device: Device) {
    this.broker.call({
      path: '/api/v1/items/' + device.id + '/commands'
    }).then(res => {
      this.store.dispatch({ type: SET_ACTION_COMMANDS, payload: res });
    }, err => {
      this.error(err);
    });
  }

  getSimpleItems(path: string): Promise<any> {
    return this.broker.call({ path: path });
  }

  set moduleRoot(route: ActivatedRoute) {
    this._moduleRoot = route;
  }

  get moduleRoot(): ActivatedRoute {
    return this._moduleRoot;
  }

  private _moduleRoot: ActivatedRoute;
}



// WEBPACK FOOTER //
// ./~/@c4/programming/src/app/common/services/shared-programming.service.ts